import { Card, CardContent } from '@/components/ui/card';
import { Quote } from 'lucide-react';

interface QuoteWidgetProps {
  config: Record<string, unknown>;
  data?: unknown;
}

const DEFAULT_QUOTES = [
  { text: "Every moment is a fresh beginning.", author: "T.S. Eliot" },
  { text: "Believe you can and you're halfway there.", author: "Theodore Roosevelt" },
  { text: "The only way to do great work is to love what you do.", author: "Steve Jobs" },
  { text: "It does not matter how slowly you go as long as you do not stop.", author: "Confucius" },
  { text: "Success is not final, failure is not fatal: it is the courage to continue that counts.", author: "Winston Churchill" },
];

export function QuoteWidget({ config, data }: QuoteWidgetProps) {
  const quotes = (config.quotes as typeof DEFAULT_QUOTES) || DEFAULT_QUOTES;
  const showAuthor = config.showAuthor !== false;
  const variant = (config.variant as 'default' | 'minimal' | 'featured') || 'default';
  
  // Use data quote if provided, otherwise pick a daily quote based on date
  const selectedQuote = (data as { text: string; author: string }) || 
    quotes[new Date().getDate() % quotes.length];

  if (variant === 'minimal') {
    return (
      <Card className="h-full bg-transparent border-none shadow-none">
        <CardContent className="p-4 flex items-center h-full">
          <div className="text-center w-full">
            <p className="text-sm italic text-muted-foreground">
              "{selectedQuote.text}"
            </p>
            {showAuthor && selectedQuote.author && (
              <p className="text-xs text-muted-foreground/70 mt-1">
                — {selectedQuote.author}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (variant === 'featured') {
    return (
      <Card className="h-full bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
        <CardContent className="p-6 flex flex-col justify-center h-full">
          <Quote className="h-8 w-8 text-primary/40 mb-3" />
          <p className="text-lg font-medium leading-relaxed">
            "{selectedQuote.text}"
          </p>
          {showAuthor && selectedQuote.author && (
            <p className="text-sm text-muted-foreground mt-3">
              — {selectedQuote.author}
            </p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardContent className="p-4 flex flex-col justify-center h-full">
        <div className="flex gap-3">
          <Quote className="h-5 w-5 text-primary/60 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm leading-relaxed">
              "{selectedQuote.text}"
            </p>
            {showAuthor && selectedQuote.author && (
              <p className="text-xs text-muted-foreground mt-2">
                — {selectedQuote.author}
              </p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
